import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "src/redux/store";
import ATMPagination from "src/components/UI/atoms/ATMPagination/ATMPagination";
import ATMTable, {
  columnTypes,
} from "src/components/UI/atoms/ATMTable/ATMTable";
import ATMTableHeader from "src/components/UI/atoms/ATMTableHeader/ATMTableHeader";
import {
  setRowsPerPage,
  setPage,
  setSearchValue,
  setOrderByValue
} from "src/redux/slices/PANApplication/PANFormFillPendingApplicationSlice";
import { PANListResponse } from "src/models/PAN.model";
import AuthHOC from "src/userAccess/AuthHOC";
import {AccessAction} from "src/utils/Enums/AccessAction"
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";

type Props = {
  columns: columnTypes[];
  rows: any[];
  /** SKIP_FORM_FILL. Gates the selection column and the bulk action together. */
  canSkipFormFill: boolean;
  selectedRows: PANListResponse[];
  setSelectedRows: React.Dispatch<React.SetStateAction<PANListResponse[]>>;
  isBulkSkipping: boolean;
  onBulkSkip: () => void;
};

const PANFormFillPendingApplicationListing = ({
  columns,
  rows,
  canSkipFormFill,
  selectedRows,
  setSelectedRows,
  isBulkSkipping,
  onBulkSkip,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const PANFormFillPendingApplicationState: any = useSelector(
    (state: RootState) => state.PANFormFillPendingApplication
  );

  const { page, rowsPerPage, isTableLoading, searchValue, totalItems, orderByValue } =
    PANFormFillPendingApplicationState;

  return (
   
      <div className="border flex flex-col overflow-auto rounded bg-white ">
        {/*Table Header */}
        <ATMTableHeader
          page={page}
          rowCount={totalItems}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onRowsPerPageChange={(newValue) => dispatch(setRowsPerPage(newValue))}
          searchValue={searchValue}
          onSearchChange={(newValue) => dispatch(setSearchValue(newValue))}
          orderValue={orderByValue}
          onOrderByChange={(newValue)=>dispatch(setOrderByValue(newValue))} 
          isShowOrder={true}
        />

        {/*
          Bulk action bar. It only takes up space once something is ticked, so
          the table looks unchanged for admins who never use it — and it is
          absent entirely without SKIP_FORM_FILL, since the checkboxes that
          feed it are gated on the same grant.
        */}
        {canSkipFormFill && selectedRows?.length ? (
          <div className="flex items-center justify-between gap-3 px-4 py-2 border-t bg-amber-50">
            <span className="text-[13px] text-slate-600">
              {selectedRows.length} application
              {selectedRows.length > 1 ? "s" : ""} selected
            </span>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-[13px] text-slate-500 underline"
                onClick={() => setSelectedRows([])}
              >
                Clear
              </button>

              <ATMLoadingButton
                type="button"
                loadingText="Please wait..."
                isLoading={isBulkSkipping}
                disabled={isBulkSkipping}
                className="bg-amber-500 w-40 p-0 py-[6px] text-[12px]"
                onClick={onBulkSkip}
              >
                SKIP FORM FILL
              </ATMLoadingButton>
            </div>
          </div>
        ) : null}

        {/* Table */}
        <div className="border flex flex-col grow overflow-auto rounded bg-white">
          <ATMTable
            columns={columns}
            isLoading={isTableLoading}
            rows={rows}
            isCheckbox={canSkipFormFill}
            selectedRows={selectedRows}
            onRowSelect={(selectedRows) => setSelectedRows(selectedRows)}
            disableRowClick={
              !AuthHOC({
                type: "ACTION",
                moduleName: "PAN_APPLICATIONS",
                action: AccessAction.APPLIACTION_VIEW_ON_CLICK,
                resultType: "BOOLEAN",
              })
            }
            rowExtraClasses={(row) => "min-h-[40px]"}
            onRowClick={(row: PANListResponse) => navigate(`/pan/${row._id}`)}
          />
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-end border-t border-slate-300">
          <ATMPagination
            page={page}
            rowCount={totalItems}
            rows={rows}
            rowsPerPage={rowsPerPage}
            onPageChange={(newPage) => dispatch(setPage(newPage))}
          />
        </div>
      </div>
  );
};

export default PANFormFillPendingApplicationListing;
